import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Download, Share2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { slugify, wallpaperFilename } from "@/data/pillarJourney";
import { useSEO } from "@/components/Seo";
import { btnGold, eyebrow } from "@/lib/styles";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "freedom", label: "Freedom" },
  { key: "chaos", label: "Chaos" },
  { key: "truth", label: "Truth" },
  { key: "grit", label: "Grit" },
  { key: "rebellion", label: "Rebellion" },
  { key: "wisdom", label: "Wisdom" },
];

export default function FreeWallpapers() {
  useSEO(
    "Free Phone Wallpapers | Threads Unbound™",
    "Free Threads Unbound™ phone wallpapers. Six original artworks, six pillars. Free for personal use."
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [artworks, setArtworks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/artworks").then((r) => setArtworks(r.data || [])).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const walls = artworks.filter((a) => a.wallpaper_url);
  const filtered = filter === "all" ? walls : walls.filter((a) => a.pillar === filter);

  useEffect(() => {
    const piece = searchParams.get("piece");
    if (piece && walls.length) {
      const found = walls.find((a) => slugify(a.title) === piece);
      if (found) setOpen(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, artworks.length]);

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    const onKey = (e) => e.key === "Escape" && closeModal();
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.__lenis?.start();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const closeModal = () => {
    setOpen(null);
    setSearchParams({}, { replace: true });
  };

  const sharePiece = async (a) => {
    const url = `${window.location.origin}/free-wallpapers?piece=${slugify(a.title)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${a.title} | Threads Unbound™`, text: `Free wallpaper: ${a.title} by Threads Unbound.`, url });
        return;
      } catch (e) {
        /* dismissed */
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch (e) {
      toast.error(url);
    }
  };

  return (
    <main className="px-6 pb-32 pt-36 md:px-12 md:pt-44 lg:px-20" data-testid="wallpapers-page">
      <header>
        <MaskedLine>
          <span className={eyebrow}>Yours to carry</span>
        </MaskedLine>
        <h1 className="mt-6 font-serif leading-[0.95] tracking-tighter">
          <MaskedLine delay={0.15}>
            <span className="block text-5xl font-light sm:text-6xl lg:text-7xl">
              Free <span className="italic text-[#D4AF37]">Wallpapers</span>
            </span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.3}>
          <p className="mt-8 max-w-xl text-base leading-relaxed text-white/60">
            Six artworks. Six pillars. Phone-sized and ready for your lock screen. Free for personal use.
          </p>
        </Reveal>
        <Reveal delay={0.4}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="wallpaper-filters">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`wallpaper-filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`min-h-[44px] border px-5 py-2 text-[10px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                  filter === f.key
                    ? "border-[#D4AF37] text-[#D4AF37]"
                    : "border-white/15 text-white/40 hover:border-white/40 hover:text-white/70"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>
      </header>

      <section className="mt-14 grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-3" data-testid="wallpaper-grid">
        {filtered.map((a, i) => (
          <Reveal key={a.id} delay={(i % 3) * 0.08}>
            <article className="group" data-testid={`wallpaper-card-${slugify(a.title)}`}>
              <button
                onClick={() => setOpen(a)}
                aria-label={`Preview ${a.title} wallpaper`}
                data-testid={`wallpaper-preview-${slugify(a.title)}`}
                className="relative block aspect-[9/19] w-full overflow-hidden border border-white/10"
              >
                <img
                  src={a.wallpaper_preview_url || a.wallpaper_url}
                  alt={`${a.title} phone wallpaper`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
              </button>
              <div className="mt-4">
                <p className="text-[9px] uppercase tracking-[0.3em] text-[#D4AF37]/80">{a.pillar}</p>
                <h2 className="mt-1 font-serif text-xl font-light">{a.title}</h2>
                <p className="mt-1 text-xs leading-relaxed text-white/50">{a.description}</p>
                <div className="mt-4 flex items-center gap-3">
                  <a
                    href={`${a.wallpaper_url}?download=${wallpaperFilename(a.title)}`}
                    download={wallpaperFilename(a.title)}
                    data-testid={`wallpaper-download-${slugify(a.title)}`}
                    className="inline-flex min-h-[44px] items-center gap-2 border border-[#D4AF37]/60 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] transition-colors duration-300 hover:bg-[#D4AF37] hover:text-black"
                  >
                    <Download className="h-3.5 w-3.5" /> Download
                  </a>
                  <span className="text-[10px] tracking-wide text-white/30">Free for personal use</span>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </section>

      {settings?.substack_url && (
        <section className="mt-24 border-t border-white/10 pt-16 text-center" data-testid="wallpaper-substack-invite">
          <Reveal>
            <p className="font-serif text-2xl font-light md:text-3xl">
              Get future artwork <span className="italic text-[#D4AF37]">releases</span>
            </p>
            <p className="mx-auto mt-4 max-w-md text-sm text-white/50">
              New pieces and wallpapers go to the letter first. No noise.
            </p>
            <a
              href={settings.substack_url}
              target="_blank"
              rel="noreferrer"
              data-testid="wallpaper-substack-button"
              className={`${btnGold} mt-8`}
            >
              Subscribe on Substack <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </section>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="wallpaper-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] flex items-center justify-center p-6 backdrop-blur-md"
            style={{ backgroundColor: "rgba(0,0,0,0.92)" }}
            onClick={closeModal}
          >
            <button
              data-testid="wallpaper-modal-close"
              aria-label="Close preview"
              onClick={closeModal}
              className="absolute right-6 top-6 border border-white/20 p-2.5 text-white/70 transition-colors duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              key={open.id}
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="max-h-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={open.wallpaper_url}
                alt={`${open.title} wallpaper full preview`}
                data-testid="wallpaper-modal-image"
                className="mx-auto max-h-[68vh] w-auto max-w-full border border-white/15 object-contain"
              />
              <p className="mt-4 font-serif text-2xl font-light">{open.title}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80">{open.pillar}</p>
              <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                <a
                  href={`${open.wallpaper_url}?download=${wallpaperFilename(open.title)}`}
                  download={wallpaperFilename(open.title)}
                  data-testid="wallpaper-modal-download"
                  className={btnGold}
                >
                  <Download className="h-4 w-4" /> Download
                </a>
                <button
                  onClick={() => sharePiece(open)}
                  data-testid="wallpaper-modal-share"
                  className="inline-flex min-h-[48px] items-center gap-3 border border-white/25 px-6 py-3 text-xs uppercase tracking-[0.25em] text-white/80 transition-colors duration-300 hover:border-white hover:text-white"
                >
                  <Share2 className="h-4 w-4" /> Share
                </button>
              </div>
              <p className="mt-4 text-xs tracking-wide text-white/30">
                Free for personal use. On mobile, the image may open full-screen: long-press to save.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
